import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Bell,
  Calendar,
  CheckCheck,
  Clock,
  Loader2,
  TrendingUp,
} from "lucide-react";
import { AppLayout } from "../components/layout/AppLayout.js";
import { Header } from "../components/layout/Header.js";
import { type AppNotification, type NotificationRule, notificationsApi } from "../lib/api.js";

const RULE_META: Record<NotificationRule, { icon: React.ReactNode; color: string; label: string }> =
  {
    anniversary_12m: {
      icon: <Calendar size={14} />,
      color: "var(--accent-gold)",
      label: "12-Monats-Jubiläum",
    },
    anniversary_24m: {
      icon: <Calendar size={14} />,
      color: "var(--accent-gold)",
      label: "24-Monats-Jubiläum",
    },
    score_worsened: {
      icon: <TrendingUp size={14} />,
      color: "var(--risk-high)",
      label: "Score verschlechtert",
    },
    no_action_30d: {
      icon: <AlertTriangle size={14} />,
      color: "var(--accent)",
      label: "Keine Maßnahme",
    },
  };

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / (1000 * 60 * 60));
  if (h < 24) return h === 0 ? "Gerade eben" : `vor ${h}h`;
  const d = Math.floor(h / 24);
  return d === 1 ? "Gestern" : `vor ${d} Tagen`;
}

export function Notifications() {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["notifications"],
    queryFn: notificationsApi.getUnseen,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const markSeenMutation = useMutation({
    mutationFn: notificationsApi.markSeen,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: async () => {
      const notifications = data?.notifications ?? [];
      await Promise.all(notifications.map((n) => notificationsApi.markSeen(n.id)));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const notifications = data?.notifications ?? [];
  const unseenCount = data?.unseenCount ?? 0;

  function groupByRule(notifs: AppNotification[]) {
    const groups = new Map<NotificationRule, AppNotification[]>();
    for (const n of notifs) {
      const group = groups.get(n.rule) ?? [];
      group.push(n);
      groups.set(n.rule, group);
    }
    return groups;
  }

  const grouped = groupByRule(notifications);

  return (
    <AppLayout>
      <Header
        title="Benachrichtigungen"
        subtitle="Regelbasierte FK-Alerts · automatisch ausgelöst"
      />
      <div className="flex-1 overflow-y-auto px-6 py-5 page-enter">
        {/* Summary bar */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded flex items-center justify-center"
              style={{
                background: unseenCount > 0 ? "rgba(234,91,11,0.12)" : "var(--bg-hover)",
                color: "var(--accent)",
              }}
            >
              <Bell size={14} />
            </div>
            <span className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>
              {isLoading
                ? "Wird geladen…"
                : unseenCount > 0
                  ? `${unseenCount} ungelesen${unseenCount > 1 ? "e Benachrichtigungen" : "e Benachrichtigung"}`
                  : "Keine neuen Benachrichtigungen"}
            </span>
          </div>
          {unseenCount > 0 && (
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
              style={{ border: "1px solid var(--border)", color: "var(--text-muted)" }}
              onClick={() => markAllMutation.mutate()}
              disabled={markAllMutation.isPending}
            >
              <CheckCheck size={11} />
              Alle als gelesen markieren
            </button>
          )}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center h-40 gap-2">
            <Loader2 size={16} className="animate-spin" style={{ color: "var(--accent)" }} />
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              Regeln werden ausgewertet…
            </span>
          </div>
        )}

        {isError && (
          <div
            className="flex items-center gap-2 p-4 rounded-lg"
            style={{ background: "var(--risk-high-bg)", border: "1px solid rgba(240,78,78,0.2)" }}
          >
            <AlertTriangle size={14} style={{ color: "var(--risk-high)" }} />
            <span className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Benachrichtigungen nicht verfügbar.
            </span>
          </div>
        )}

        {!isLoading && !isError && unseenCount === 0 && (
          <div
            className="flex flex-col items-center justify-center h-48 gap-3 rounded-lg"
            style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
          >
            <CheckCheck size={24} style={{ color: "var(--risk-low)" }} />
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Alle Benachrichtigungen gelesen
            </p>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              Neue Alerts werden automatisch generiert wenn Regeln zutreffen
            </p>
          </div>
        )}

        {/* Grouped notifications */}
        {!isLoading && !isError && unseenCount > 0 && (
          <div className="flex flex-col gap-4">
            {[...grouped.entries()].map(([rule, items]) => {
              const meta = RULE_META[rule];
              return (
                <div key={rule} className="card overflow-hidden">
                  <div
                    className="flex items-center gap-2 px-4 py-2.5"
                    style={{
                      borderBottom: "1px solid var(--border)",
                      background: "var(--bg-surface)",
                    }}
                  >
                    <span style={{ color: meta.color }}>{meta.icon}</span>
                    <span className="text-xs font-semibold" style={{ color: meta.color }}>
                      {meta.label}
                    </span>
                    <span
                      className="ml-auto text-[10px] px-1.5 py-0.5 rounded font-mono-data"
                      style={{ background: `${meta.color}18`, color: meta.color }}
                    >
                      {items.length}
                    </span>
                  </div>
                  <div>
                    {items.map((n) => (
                      <div
                        key={n.id}
                        className="flex items-start gap-3 px-4 py-3"
                        style={{ borderBottom: "1px solid var(--border)" }}
                      >
                        <div
                          className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                          style={{ background: n.seen ? "var(--text-muted)" : meta.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-xs leading-relaxed"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            {n.message}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span
                            className="flex items-center gap-1 text-[10px]"
                            style={{ color: "var(--text-muted)" }}
                          >
                            <Clock size={9} />
                            {timeAgo(n.triggeredAt)}
                          </span>
                          {!n.seen && (
                            <button
                              type="button"
                              className="text-[9px] px-1.5 py-0.5 rounded"
                              style={{
                                border: "1px solid var(--border)",
                                color: "var(--text-muted)",
                              }}
                              onClick={() => markSeenMutation.mutate(n.id)}
                            >
                              ✓
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
